// The clock: the legacy GoshClock and its four views, drawn from the form
// descriptor.
//
// The legacy component was handed three booleans and chose between
// DateRangeOnlyView (a date announcement: two dates, two times labelled
// "Uhrzeit"), DateRangeView (two dates, "Startzeit" and "Endzeit"),
// StartDateOnlyView (a start date and time, and two greyed-out fields saying
// "Kein Enddatum") and TimeOnlyView (one date, "Startzeit" and "Endzeit").
// Under all four, "Dauer: …". The same four are here, in the same Grid; what
// chooses between them is what the server said about the fields.
//
// One thing is new against the legacy view: an announcement without a return
// date may now be given one after all. The greyed-out "Kein Enddatum" fields
// are still drawn — they are what the person expects to see — and a checkbox
// under them turns them into real ones.

import { Box, Checkbox, FormControlLabel, Grid, TextField, Typography } from "@mui/material";
import type { FormDescriptor } from "~/api";
import { useT } from "~/i18n";
import { DateField, DateRangeField } from "./calendar";
import {
  addDays,
  addMinutes,
  changeDay,
  changeEndDay,
  changeEndTime,
  changeRange,
  changeStartDay,
  changeStartTime,
  changeStartTimeKeepingLength,
  durationText,
  floorQuarter,
  format,
  startOfDay,
  parse,
  viennaNow,
  type Mode,
  type Window,
} from "./dates";
import { useBelowSm } from "./media";
import { TimeField } from "./timewheel";

/** modeOf reads the legacy three booleans back out of the descriptor. */
export function modeOf(d: FormDescriptor): Mode {
  if (d.kind === "timer") return "time";
  if (d.end?.allowOpenEnded) return "open";
  if (d.start?.granularity === "day") return "date";
  return "datetime";
}

/**
 * defaultWindow is where the form starts, as the legacy drawer and its views
 * left it once a type was chosen: a date announcement from midnight today to
 * midnight tomorrow; a date and time from the quarter hour that is running to
 * a day and a quarter hour later (the drawer's quarter hour, which
 * DateRangeView then pushed a day on); a time announcement for a quarter hour;
 * and no end for the one that needs none.
 */
export function defaultWindow(mode: Mode, now = viennaNow()): Window {
  const start = floorQuarter(now);
  switch (mode) {
    case "time":
      return { start, end: addMinutes(start, 15) };
    case "open":
      return { start, end: null };
    case "date": {
      const midnight = startOfDay(now);
      return { start: midnight, end: addDays(midnight, 1) };
    }
    default:
      return { start, end: addMinutes(addDays(start, 1), 15) };
  }
}

/** The invisible "_" the legacy views put between two time fields. */
function Separator() {
  return (
    <Grid item xs={1} sx={{ visibility: "hidden" }}>
      <Box sx={{ display: "inline-block", transform: "scale(1.2)", pl: 1.5, pt: 1 }}>_</Box>
    </Grid>
  );
}

export function Clock({
  descriptor,
  startsAt,
  endsAt,
  onChange,
}: {
  descriptor: FormDescriptor;
  startsAt: string | null;
  endsAt: string | null;
  onChange: (startsAt: string, endsAt: string | null) => void;
}) {
  const t = useT();
  const belowsm = useBelowSm();
  const mode = modeOf(descriptor);

  const fallback = defaultWindow(mode);
  const start = parse(startsAt) ?? fallback.start;
  const end = parse(endsAt) ?? (mode === "open" ? null : fallback.end);
  const w: Window = { start, end };
  const set = (next: Window) => onChange(format(next.start), next.end ? format(next.end) : null);

  const startLabel = t(descriptor.start?.labelKey ?? "startdate");
  const endLabel = t(descriptor.end?.labelKey ?? "enddate");

  let view: React.ReactNode;
  switch (mode) {
    // DateRangeOnlyView and DateRangeView.
    case "date":
    case "datetime": {
      const ends = w.end ?? fallback.end!;
      view = (
        <>
          <Grid item xs={3} sx={{ p: 1, pb: mode === "date" && belowsm ? 0 : 2, ml: 1 }}>
            <Grid container>
              <Grid item xs={12}>
                <DateRangeField
                  startLabel={startLabel}
                  endLabel={endLabel}
                  value={[w.start, ends]}
                  onChange={(days) => set(changeRange(w, days))}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3} sx={{ p: 1, pt: mode === "date" && belowsm ? 2 : 0, ml: 1 }}>
            <Grid container>
              <Grid item xs={5.5} sx={{ pr: 0.5 }}>
                <TimeField
                  label={t(mode === "date" ? "time" : "start-time")}
                  value={w.start}
                  onChange={(time) => set(changeStartTime(w, time))}
                />
              </Grid>
              <Separator />
              <Grid item xs={5.5} sx={{ pl: 0.5 }}>
                <TimeField
                  label={t(mode === "date" ? "time" : "end-time")}
                  value={ends}
                  onChange={(time) => set(changeEndTime(w, time))}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      );
      break;
    }

    // TimeOnlyView.
    case "time": {
      const ends = w.end ?? fallback.end!;
      view = (
        <>
          <Grid item xs={3} sx={{ p: 1, pb: 2, ml: 1 }}>
            <Grid container>
              <Grid item xs={12}>
                <DateField label={t("date")} value={w.start} onChange={(day) => set(changeDay(w, day))} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3} sx={{ p: 1, pt: 0, ml: 1 }}>
            <Grid container>
              <Grid item xs={5.5} sx={{ pr: 0.5 }}>
                <TimeField
                  label={t("start-time")}
                  value={w.start}
                  showIcon
                  onChange={(time) => set(changeStartTimeKeepingLength(w, time))}
                />
              </Grid>
              <Separator />
              <Grid item xs={5.5} sx={{ pl: 0.5 }}>
                <TimeField label={t("end-time")} value={ends} showIcon onChange={(time) => set(changeEndTime(w, time))} />
              </Grid>
            </Grid>
          </Grid>
        </>
      );
      break;
    }

    // StartDateOnlyView.
    case "open": {
      const row = (label: string, value: Date, onDay: (d: Date) => void, onTime: (d: Date) => void) => (
        <Grid container>
          <Grid item xs={6.5}>
            <DateField label={label} value={value} onChange={onDay} />
          </Grid>
          <Grid item xs={5.5}>
            {!belowsm ? (
              <Box sx={{ ml: 1.5 }}>
                <TimeField label={t("time")} value={value} showIcon onChange={onTime} />
              </Box>
            ) : (
              <TimeField label={t("time")} value={value} haveMargin center onChange={onTime} />
            )}
          </Grid>
        </Grid>
      );
      view = (
        <>
          <Grid item xs={3} sx={{ p: 1, pb: 0, ml: 1 }}>
            {row(startLabel, w.start, (day) => set(changeStartDay(w, day)), (time) => set(changeStartTime(w, time)))}
          </Grid>
          <Grid item xs={3} sx={{ p: 1, pt: belowsm && !w.end ? 0 : 2, ml: 1 }}>
            {w.end ? (
              row(endLabel, w.end, (day) => set(changeEndDay(w, day)), (time) => set(changeEndTime(w, time)))
            ) : (
              <Grid container sx={{ display: belowsm ? "none" : "flex" }}>
                <Grid item xs={6.5}>
                  <TextField variant="outlined" value={t("no-enddate")} fullWidth disabled />
                </Grid>
                <Grid item xs={5.5}>
                  <TextField variant="outlined" value="" disabled sx={{ ml: 1.5 }} />
                </Grid>
              </Grid>
            )}
            {descriptor.end?.allowOpenEnded ? (
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={!w.end}
                    onChange={(e) => set({ start: w.start, end: e.target.checked ? null : addDays(w.start, 1) })}
                  />
                }
                label={<Typography sx={{ fontSize: 14 }}>{t("no-enddate")}</Typography>}
              />
            ) : null}
          </Grid>
        </>
      );
      break;
    }
  }

  return (
    <Grid container direction="column">
      {view}
      <Grid item justifyContent="center" sx={{ display: "flex" }}>
        <Typography sx={{ color: "text.secondary" }}>{`${t("duration")}: ${durationText(w, mode, t)}`}</Typography>
      </Grid>
    </Grid>
  );
}
